import React, {useEffect, useRef} from 'react';
import SlideModal from '@/Atoms/SlideModal';
import useOnboardingSheetStore from '@/Store/onboardingSheetStore';
import useOnboardingProgressStore, {
  useOnboardingProgress,
} from '@/Store/onboardingProgressStore';
import useUpdateLearningOnboardingStatusApi from '@/Hooks/UpdateOnboardingStatus/useUpdateLearningOnboardingStatusApi';
import ChecklistBody from './ChecklistBody';

interface Props {
  navigationRef?: any;
}

// Global singleton — mounted once in Application.tsx (AlertModal pattern).
// Any surface opens it via useOnboardingSheetStore.getState().openSheet().
//
// This shell is always dismissable. The blocking variant used in School of
// Chess is a separate in-screen overlay (Screens/Books/CsocProfileGate), not a
// mode of this modal, because a screen-covering modal also covers the left menu
// and would strand the user.
export default function OnboardingChecklistSheet({navigationRef}: Props) {
  const {isOpen, source, closeSheet} = useOnboardingSheetStore();
  const {percent} = useOnboardingProgress();

  // Persist learning_onboarding_status=1 the moment the checklist hits 100%,
  // so the backend catches up with the client-derived completion instead of
  // staying at whatever the user logged in with (0/null, or 4 for the
  // fast-path login). This component is a global singleton (mounted once in
  // Application.tsx, hooks run regardless of isOpen), so it's a single place
  // to react to the transition. `statusPersisted` is a persisted latch
  // (onboardingProgressStore, same idiom as the welcome nudge), but it's only
  // set from the mutation's onSuccess -- a transient failure (network blip,
  // 5xx) must NOT permanently block the write, so the next time this effect
  // re-evaluates (e.g. the user reloads/relogs in and hydrated+hasSummary
  // flip false->true again) it retries. Double-fire while a request is still
  // in flight is guarded separately by inFlightRef, not the latch.
  const hydrated = useOnboardingProgressStore(s => s.hydrated);
  const statusPersisted = useOnboardingProgressStore(s => s.statusPersisted);
  const hasSummary = useOnboardingProgressStore(s => !!s.summarySnapshot?.user);
  const currentStatus = useOnboardingProgressStore(
    s => s.summarySnapshot?.user?.learning_onboarding_status,
  );
  const markStatusPersisted = useOnboardingProgressStore(
    s => s.markStatusPersisted,
  );
  const inFlightRef = useRef(false);
  const {mutate: persistOnboardedStatus} = useUpdateLearningOnboardingStatusApi(
    () => {
      markStatusPersisted();
    },
  );

  useEffect(() => {
    if (!hydrated || !hasSummary || statusPersisted || percent < 100) return;
    // Only advance the never-onboarded (0/null) and fast-path (4) statuses --
    // 2 (analysis-pending) and 3 (checkout) are owned by other flows.
    if (currentStatus !== 0 && currentStatus !== null && currentStatus !== 4) {
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    persistOnboardedStatus(undefined, {
      onSettled: () => {
        inFlightRef.current = false;
      },
    });
  }, [
    hydrated,
    hasSummary,
    statusPersisted,
    percent,
    currentStatus,
    persistOnboardedStatus,
  ]);

  const go = (route: string) => {
    closeSheet();
    if (route) {
      navigationRef?.navigate?.(route);
    }
  };

  const title =
    source === 'welcome'
      ? "Welcome — let's get you set up"
      : percent >= 100
      ? "You're all set"
      : 'Finish your profile';

  if (!isOpen) {
    return null;
  }

  return (
    <SlideModal
      visible={isOpen}
      type="center"
      showCloseIcon
      onClose={() => closeSheet()}
      modalHeaderTitle={title}>
      <ChecklistBody onNavigate={go} onDismiss={() => closeSheet()} />
    </SlideModal>
  );
}
